import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import activationKeyService from '../services/activationKeyService'

export const activationKeyQueryKeys = {
  solutionAccess: (userId: string | undefined) => ['solution-access', userId] as const,
  keys: (userId: string | undefined) => ['activation-keys', userId] as const,
}

export function useSolutionAccess(userId: string | undefined) {
  return useQuery({
    queryKey: activationKeyQueryKeys.solutionAccess(userId),
    queryFn: () => activationKeyService.getSolutionAccess(userId!),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userId,
    select: (res) => res.data,
  })
}

/** Activation keys the user has redeemed (status REDEEMED or EXPIRED) */
export function useActivationKeys(userId: string | undefined) {
  return useQuery({
    queryKey: activationKeyQueryKeys.keys(userId),
    queryFn: () => activationKeyService.getActivationKeys(userId!),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userId,
    select: (res) => res.data.keys,
  })
}

/**
 * Whether the user holds any REDEEMED activation key, i.e. has bought a test series.
 * Users without one are treated as free users.
 */
export function useHasRedeemedKey(userId: string | undefined) {
  const { data: keys, isPending, isError, refetch } = useActivationKeys(userId)
  return {
    hasRedeemedKey: keys?.some((key) => key.status === 'REDEEMED') ?? false,
    isLoading: isPending,
    isError,
    refetch,
  }
}

export function useRedeemKey(userId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => activationKeyService.redeemKey(userId, code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activationKeyQueryKeys.solutionAccess(userId) })
      queryClient.invalidateQueries({ queryKey: activationKeyQueryKeys.keys(userId) })
    },
  })
}
